import {OperationVariables} from "@apollo/react-common"
import {getLogger} from "@prosay/logging"
import {UpdateQueryFn} from "apollo-client/core/watchQueryOptions"
import {Scalars} from "../graphql"

const logger = getLogger(__filename)

export interface UpdateLiveQueryOptions<TData> {
    add?: (element: TData, list: readonly TData[]) => readonly TData[]
    sort?: (lhs: TData, rhs: TData) => number
}

export const updateLiveQuery = <
    TData extends {readonly id: Scalars["ID"]},
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    TListKey extends keyof any,
    TPrevious extends {readonly [T in TListKey]: readonly TData[] | null},
    TSubscriptionData,
    TSubscriptionVariables = OperationVariables
>(
    listKey: TListKey,
    getInfo: (
        subscriptionData: TSubscriptionData,
    ) => readonly [TData, "added" | "removed" | "updated"] | undefined | null,
    {
        add = (element, list) => [element, ...list],
        sort,
    }: UpdateLiveQueryOptions<TData> = {},
): UpdateQueryFn<TPrevious, TSubscriptionVariables, TSubscriptionData> => (
    previous,
    {subscriptionData},
) => {
    const sortFn = sort
        ? (list: readonly TData[]) => [...list].sort(sort)
        : (list: readonly TData[]) => list
    const info = getInfo(subscriptionData.data)
    logger.debug`Got the following result from getInfo: ${info}`
    if (!info) {
        return previous
    }
    const prevList =
        previous[listKey] === null
            ? ([] as readonly TData[])
            : (previous[listKey] as readonly TData[])
    const [data, type] = info
    switch (type) {
        case "added":
            return {
                ...previous,
                [listKey]: sortFn(add(data, prevList)),
            }
        case "removed":
            return {
                ...previous,
                [listKey]: sortFn(prevList.filter(({id}) => id !== data.id)),
            }
        case "updated":
            return {
                ...previous,
                [listKey]: sortFn(
                    prevList.map(curr => (curr.id === data.id ? data : curr)),
                ),
            }
        default:
            return previous
    }
}
